// src/components/AIInterviewTable.js
import React, { useMemo, useState } from 'react';
import {
  Table, TableHead, TableRow, TableHeader, TableBody, TableCell,
  TableContainer, Tag
} from '@carbon/react';
import dayjs from 'dayjs';
import { LABEL_ORDER } from './aiInterviewUtils';

/** ラベルの「（」or "(" の直前で2行に割る */
function LabelWithBreak({ label }) {
  const s = String(label ?? '');
  const iFull = s.indexOf('（'); // 全角
  const iHalf = s.indexOf('(');  // 半角
  const i = (iFull !== -1) ? iFull : iHalf;

  const Inner = ({ children }) => (
    <span style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>{children}</span>
  );

  if (i === -1) return <Inner>{s}</Inner>;
  const head = s.slice(0, i);
  const tail = s.slice(i); // カッコ含めて残り

  return (
    <Inner>
      {head}
      <br />
      {tail}
    </Inner>
  );
}

export default function AIInterviewTable({
  rows = [],
  latestOnly = false,
  showRegenCount = true,
  showBaseQuestion = true,
  expandable = true,
  isNarrow = false,
  previewLen = 30,
  /** 'question' | 'row' */
  expandClickArea = 'question',
}) {
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  /** 並び順：ラベル既定順 → 日付降順（latestOnly=false） */
  const rowsToRender = useMemo(() => {
    if (!latestOnly) {
      return [...rows].sort((a, b) =>
        LABEL_ORDER.indexOf(a.label) - LABEL_ORDER.indexOf(b.label) ||
        dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
      );
    }
    // latestOnly=true：カテゴリ毎に最新1件
    const map = new Map();
    for (const r of rows) {
      const prev = map.get(r.category);
      if (!prev || dayjs(r.date).isAfter(prev.date)) map.set(r.category, r);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => LABEL_ORDER.indexOf(a.label) - LABEL_ORDER.indexOf(b.label));
    return arr;
  }, [rows, latestOnly]);

  const toggle = (key) => {
    if (!expandable) return;
    setExpandedKeys((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const renderQuestionCell = (row, key) => {
    const expanded = expandedKeys.has(key);
    const text = row.question ?? '';
    const preview = text.length > previewLen ? `${text.slice(0, previewLen)}...` : text;

    if (!expandable || expandClickArea === 'row') {
      return <span style={{ whiteSpace: 'pre-wrap' }}>{expanded ? text : preview}</span>;
    }

    return (
      <span
        onClick={() => toggle(key)}
        style={{ cursor: 'pointer', whiteSpace: 'pre-wrap' }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(key)}
        aria-expanded={expanded}
      >
        {expanded ? text : preview}
        <span style={{ marginLeft: '0.5rem' }}>{expanded ? '▲' : '▼'}</span>
      </span>
    );
  };

  // “日本語8文字分”の幅（ic単位：和文の字幅に基づく）
  const TAG_CHAR_WIDTH = '10ic';              // タグ本体の幅
  const TAG_COL_GUTTER = '4ch';              // ちょい余裕
  const TAG_COL_WIDTH = `calc(${TAG_CHAR_WIDTH} + ${TAG_COL_GUTTER})`; // 列幅

  // デスクトップは固定レイアウトで列幅を尊重
  const tableStyle = isNarrow ? {} : { tableLayout: 'fixed', width: '100%' };

  // カテゴリ列の幅（デスクトップのみ固定）
  const categoryColumnStyle = isNarrow ? {} : {
    width: TAG_COL_WIDTH,
    maxWidth: TAG_COL_WIDTH,
    minWidth: TAG_COL_WIDTH,
    textAlign: 'center',     // ← 水平センター
    verticalAlign: 'middle', // ←（任意）縦センター
  };

  // タグを8icに収める（中央揃え）
  const tagBoxStyle = {
    display: 'inline-block',
    width: TAG_CHAR_WIDTH,
    textAlign: 'center',
    verticalAlign: 'middle',
  };

  return (
    <TableContainer style={{ overflowX: 'auto' }}>
      <Table size="sm" style={tableStyle}>
        <TableHead>
          <TableRow>
            {isNarrow ? (
              <>
                <TableHeader>日付・カテゴリ</TableHeader>
                <TableHeader>質問</TableHeader>
                {showBaseQuestion ? <TableHeader>元の質問</TableHeader> : null}
              </>
            ) : (
              <>
                <TableHeader style={{ width: '11ch' }}>日付</TableHeader>
                <TableHeader style={categoryColumnStyle}>カテゴリ</TableHeader>
                <TableHeader style={{ whiteSpace: 'nowrap', width: '7ch' }}>スコア</TableHeader>
                {showRegenCount ? (
                  <TableHeader style={{ whiteSpace: 'nowrap', width: '10ch' }}>再生成回数</TableHeader>
                ) : null}
                <TableHeader>質問</TableHeader>
                {showBaseQuestion ? <TableHeader style={{ width: '24ch' }}>元の質問</TableHeader> : null}
              </>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {rowsToRender.map((row, idx) => {
            const key = `${row.category}-${row.date}-${idx}`;
            const regen = row.regenerateCount ?? 0;
            const isRowClickable = expandable && expandClickArea === 'row';

            const rowHandlers = isRowClickable
              ? {
                  onClick: () => toggle(key),
                  onKeyDown: (e) => (e.key === 'Enter' || e.key === ' ') && toggle(key),
                  role: 'button',
                  tabIndex: 0,
                  style: { cursor: 'pointer' },
                  'aria-expanded': expandedKeys.has(key),
                }
              : {};

            return (
              <TableRow key={key} {...rowHandlers}>
                {isNarrow ? (
                  <>
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span>{dayjs(row.date).format('YYYY/MM/DD')}</span>
                        <span style={tagBoxStyle}>
                          <Tag type="blue" aria-label={row.label} style={{ width: '100%' }}>
                            <LabelWithBreak label={row.label} />
                          </Tag>
                        </span>
                        <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                          <div>スコア：{row.score}</div>
                          {showRegenCount ? <div>再生成回数：{regen}</div> : null}
                        </div>
                        {expandable && expandClickArea === 'row' && (
                          <span style={{ fontSize: 12 }}>
                            {expandedKeys.has(key) ? '▲ 詳細を閉じる' : '▼ 詳細を開く'}
                          </span>
                        )}
                      </div>
                    </TableCell>

                    <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                      {renderQuestionCell(row, key)}
                    </TableCell>

                    {showBaseQuestion ? (
                      <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                        {row.baseQuestion}
                      </TableCell>
                    ) : null}
                  </>
                ) : (
                  <>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {dayjs(row.date).format('YYYY/MM/DD')}
                    </TableCell>

                    {/* カテゴリ列：8icタグ＋2ch余裕 */}
                    <TableCell style={categoryColumnStyle}>
                      <span style={tagBoxStyle}>
                        <Tag type="blue" aria-label={row.label} style={{ width: '100%' }}>
                          <LabelWithBreak label={row.label} />
                        </Tag>
                      </span>
                    </TableCell>

                    <TableCell>{row.score}</TableCell>
                    {showRegenCount ? <TableCell>{regen}</TableCell> : null}

                    <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                      {renderQuestionCell(row, key)}
                      {expandable && expandClickArea === 'row' && (
                        <span style={{ marginLeft: '0.5rem' }}>
                          {expandedKeys.has(key) ? '▲' : '▼'}
                        </span>
                      )}
                    </TableCell>

                    {showBaseQuestion ? (
                      <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                        {row.baseQuestion}
                      </TableCell>
                    ) : null}
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
